import React, { createContext, useContext, useReducer, useEffect } from 'react';
import { apiRequest } from '../utils/api';

// Data structures
const Article = {
  id: '',
  name: '',
  price: 0,
  category: ''
};

const OrderItem = {
  article: Article,
  quantity: 0,
  getSubtotal: function() {
    return this.article.price * this.quantity;
  }
};

const Order = {
  items: [],
  timestamp: Date.now(),
  addItem: function(article, quantity) {
    const existingItem = this.items.find(item => item.article.id === article.id);
    if (existingItem) {
      existingItem.quantity += quantity;
    } else {
      this.items.push({
        article,
        quantity,
        getSubtotal() {
          return this.article.price * this.quantity;
        }
      });
    }
  },
  getTotal: function() {
    return this.items.reduce((total, item) => total + item.getSubtotal(), 0);
  },
  clear: function() {
    this.items = [];
    this.timestamp = Date.now();
  }
};

// Articles data
const articles = {
  bebidas: [
    { id: 'agua_gaseosa', name: 'Agua o Gaseosa', price: 150.0, category: 'bebidas' },
    { id: 'cerveza_tirada', name: 'Cerveza Tirada', price: 200.0, category: 'bebidas' },
    { id: 'cerveza_lata', name: 'Cerveza Lata', price: 180.0, category: 'bebidas' },
    { id: 'cerveza_porron', name: 'Cerveza Porron', price: 250.0, category: 'bebidas' },
    { id: 'trago_nacional', name: 'Trago Nacional', price: 350.0, category: 'bebidas' },
    { id: 'trago_importado', name: 'Trago Importado', price: 450.0, category: 'bebidas' },
    { id: 'whisky', name: 'Whisky', price: 500.0, category: 'bebidas' },
    { id: 'whisky_importado', name: 'Whisky Importado', price: 800.0, category: 'bebidas' },
    { id: 'champagne', name: 'Champagne', price: 600.0, category: 'bebidas' },
    { id: 'champagne_premium', name: 'Champagne Premium', price: 1200.0, category: 'bebidas' },
    { id: 'chandon', name: 'Chandon', price: 400.0, category: 'bebidas' },
    { id: 'energizante', name: 'Energizante', price: 120.0, category: 'bebidas' },
    { id: 'gin_tirado', name: 'Gin Tirado', price: 300.0, category: 'bebidas' }
  ],
  comidas: [
    { id: 'papas_fritas', name: 'Papas Fritas', price: 200.0, category: 'comidas' },
    { id: 'empanada', name: 'Empanada', price: 80.0, category: 'comidas' },
    { id: 'pizza_muzzarella', name: 'Pizza Muzzarella', price: 350.0, category: 'comidas' },
    { id: 'pizza_roqufort', name: 'Pizza Roqufort', price: 400.0, category: 'comidas' },
    { id: 'pizza_jym', name: 'Pizza J y M', price: 450.0, category: 'comidas' },
    { id: 'pizza_napolitana', name: 'Pizza Napolitana', price: 380.0, category: 'comidas' },
    { id: 'sandwich_bondiola', name: 'Sandwich Bondiola desmechada', price: 320.0, category: 'comidas' },
    { id: 'sandwich_criollo', name: 'Sandwich Criollo', price: 280.0, category: 'comidas' },
    { id: 'sandwich_pampero', name: 'Sandwich Pampero', price: 300.0, category: 'comidas' }
  ]
};

// Action types
const ActionTypes = {
  ADD_ITEM: 'ADD_ITEM',
  UPDATE_QUANTITY: 'UPDATE_QUANTITY',
  REMOVE_ITEM: 'REMOVE_ITEM',
  CLEAR_ORDER: 'CLEAR_ORDER',
  UPDATE_PRICE: 'UPDATE_PRICE',
  LOAD_SALES: 'LOAD_SALES',
  ADD_SALE: 'ADD_SALE',
  LOAD_TICKET_COUNTER: 'LOAD_TICKET_COUNTER',
  INCREMENT_TICKET_COUNTER: 'INCREMENT_TICKET_COUNTER',
  RESET_TICKET_COUNTER: 'RESET_TICKET_COUNTER',
  LOAD_ARTICLES: 'LOAD_ARTICLES'
};

// Initial state
const initialState = {
  currentOrder: { items: [], timestamp: Date.now() },
  salesHistory: [],
  articles: articles,
  ticketCounter: {} // { articleId: { count: number, totalQuantity: number } }
};

// Reducer
const orderReducer = (state, action) => {
  switch (action.type) {
    case ActionTypes.ADD_ITEM:
      const newOrder = { ...state.currentOrder };
      const existingItem = newOrder.items.find(item => item.article.id === action.payload.article.id);
      
      if (existingItem) {
        existingItem.quantity += action.payload.quantity;
      } else {
        newOrder.items.push({
          article: action.payload.article,
          quantity: action.payload.quantity,
          getSubtotal() {
            return this.article.price * this.quantity;
          }
        });
      }
      
      return {
        ...state,
        currentOrder: { ...newOrder, timestamp: Date.now() }
      };

    case ActionTypes.UPDATE_QUANTITY:
      const updatedOrder = { ...state.currentOrder };
      const itemIndex = updatedOrder.items.findIndex(item => item.article.id === action.payload.articleId);
      
      if (itemIndex !== -1) {
        if (action.payload.quantity <= 0) {
          updatedOrder.items.splice(itemIndex, 1);
        } else {
          updatedOrder.items[itemIndex].quantity = action.payload.quantity;
        }
      }
      
      return {
        ...state,
        currentOrder: { ...updatedOrder, timestamp: Date.now() }
      };

    case ActionTypes.REMOVE_ITEM:
      const orderWithoutItem = { ...state.currentOrder };
      orderWithoutItem.items = orderWithoutItem.items.filter(item => item.article.id !== action.payload.articleId);
      
      return {
        ...state,
        currentOrder: { ...orderWithoutItem, timestamp: Date.now() }
      };

    case ActionTypes.CLEAR_ORDER:
      return {
        ...state,
        currentOrder: { items: [], timestamp: Date.now() }
      };

    case ActionTypes.UPDATE_PRICE:
      const updatedArticles = { ...state.articles };
      const { articleId: priceArticleId, newPrice } = action.payload;
      
      // Search for the article in all categories
      Object.keys(updatedArticles).forEach(category => {
        const articleIndex = updatedArticles[category].findIndex(article => article.id === priceArticleId);
        if (articleIndex !== -1) {
          updatedArticles[category][articleIndex] = {
            ...updatedArticles[category][articleIndex],
            price: newPrice
          };
        }
      });
      
      // Save to localStorage
      try {
        localStorage.setItem('drinkTicketArticles', JSON.stringify(updatedArticles));
      } catch (error) {
        console.error('Error saving articles to localStorage:', error);
      }
      
      return {
        ...state,
        articles: updatedArticles
      };

    case ActionTypes.LOAD_ARTICLES:
      return {
        ...state,
        articles: action.payload || state.articles
      };

    case ActionTypes.LOAD_SALES:
      return {
        ...state,
        salesHistory: action.payload
      };

    case ActionTypes.ADD_SALE:
      const newSalesHistory = [...state.salesHistory, action.payload];
      
      // Save to localStorage
      try {
        localStorage.setItem('drinkTicketSales', JSON.stringify(newSalesHistory));
      } catch (error) {
        console.error('Error saving sales to localStorage:', error);
      }
      
      return {
        ...state,
        salesHistory: newSalesHistory
      };

    case ActionTypes.LOAD_TICKET_COUNTER:
      return {
        ...state,
        ticketCounter: action.payload || {}
      };

    case ActionTypes.INCREMENT_TICKET_COUNTER:
      const { articleId: counterArticleId, quantity } = action.payload;
      const currentCounter = state.ticketCounter[counterArticleId] || { count: 0, totalQuantity: 0 };
      const newCounter = {
        ...state.ticketCounter,
        [counterArticleId]: {
          count: currentCounter.count + 1,
          totalQuantity: currentCounter.totalQuantity + quantity
        }
      };
      
      // Save to localStorage
      try {
        localStorage.setItem('drinkTicketCounter', JSON.stringify(newCounter));
      } catch (error) {
        console.error('Error saving ticket counter to localStorage:', error);
      }
      
      return {
        ...state,
        ticketCounter: newCounter
      };

    case ActionTypes.RESET_TICKET_COUNTER:
      const resetCounter = {};
      
      // Save to localStorage
      try {
        localStorage.setItem('drinkTicketCounter', JSON.stringify(resetCounter));
      } catch (error) {
        console.error('Error resetting ticket counter:', error);
      }
      
      return {
        ...state,
        ticketCounter: resetCounter
      };

    default:
      return state;
  }
};

// Context
const OrderContext = createContext();

// Provider
export const OrderProvider = ({ children }) => {
  const [state, dispatch] = useReducer(orderReducer, initialState);

  const refreshArticles = async () => {
    const data = await apiRequest('articles');
    const filteredArticles = {};
    Object.keys(data.articles).forEach(category => {
      filteredArticles[category] = data.articles[category].filter(article => article.active);
    });
    dispatch({ type: ActionTypes.LOAD_ARTICLES, payload: filteredArticles });
  };

  // Load sales history, ticket counter, and articles from localStorage on mount
  useEffect(() => {
    refreshArticles().catch(error => {
      console.error('Error loading articles from API:', error);
    });

    try {
      const savedArticles = localStorage.getItem('drinkTicketArticles');
      if (savedArticles) {
        dispatch({ type: ActionTypes.LOAD_ARTICLES, payload: JSON.parse(savedArticles) });
      }
    } catch (error) {
      console.error('Error loading articles from localStorage:', error);
    }

    try {
      const savedSales = localStorage.getItem('drinkTicketSales');
      if (savedSales) {
        dispatch({ type: ActionTypes.LOAD_SALES, payload: JSON.parse(savedSales) });
      }
    } catch (error) {
      console.error('Error loading sales from localStorage:', error);
    }

    try {
      const savedCounter = localStorage.getItem('drinkTicketCounter');
      if (savedCounter) {
        dispatch({ type: ActionTypes.LOAD_TICKET_COUNTER, payload: JSON.parse(savedCounter) });
      }
    } catch (error) {
      console.error('Error loading ticket counter from localStorage:', error);
    }
  }, []);

  // Actions
  const addItem = (article, quantity) => {
    dispatch({ type: ActionTypes.ADD_ITEM, payload: { article, quantity } });
  };

  const updateQuantity = (articleId, quantity) => {
    dispatch({ type: ActionTypes.UPDATE_QUANTITY, payload: { articleId, quantity } });
  };

  const removeItem = (articleId) => {
    dispatch({ type: ActionTypes.REMOVE_ITEM, payload: { articleId } });
  };

  const clearOrder = () => {
    dispatch({ type: ActionTypes.CLEAR_ORDER });
  };

  const updatePrice = async (articleId, newPrice) => {
    await apiRequest(`articles/${articleId}/price`, {
      method: 'PUT',
      body: JSON.stringify({ price: newPrice })
    });
    dispatch({ type: ActionTypes.UPDATE_PRICE, payload: { articleId, newPrice } });
  };

  const saveSale = (order) => {
    const sale = {
      ...order,
      id: Date.now().toString(),
      date: new Date().toISOString()
    };
    dispatch({ type: ActionTypes.ADD_SALE, payload: sale });
  };

  const getOrderTotal = () => {
    return state.currentOrder.items.reduce((total, item) => total + (item.article.price * item.quantity), 0);
  };

  const getArticlesByCategory = (category) => {
    return state.articles[category] || [];
  };

  const incrementTicketCounter = (articleId, quantity) => {
    dispatch({ type: ActionTypes.INCREMENT_TICKET_COUNTER, payload: { articleId, quantity } });
  };

  const resetTicketCounter = () => {
    dispatch({ type: ActionTypes.RESET_TICKET_COUNTER });
  };

  const getTicketCounterStats = () => {
    return state.ticketCounter;
  };

  const getArticleTicketStats = (articleId) => {
    return state.ticketCounter[articleId] || { count: 0, totalQuantity: 0 };
  };

  return (
    <OrderContext.Provider value={{
      currentOrder: state.currentOrder,
      salesHistory: state.salesHistory,
      articles: state.articles,
      ticketCounter: state.ticketCounter,
      addItem,
      updateQuantity,
      removeItem,
      clearOrder,
      updatePrice,
      saveSale,
      getOrderTotal,
      getArticlesByCategory,
      incrementTicketCounter,
      resetTicketCounter,
      getTicketCounterStats,
      getArticleTicketStats,
      refreshArticles
    }}>
      {children}
    </OrderContext.Provider>
  );
};

// Hook
export const useOrder = () => {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrder must be used within an OrderProvider');
  }
  return context;
};
